// @flow

import * as React from 'react';

import clsx from 'clsx';
import { connect } from 'react-redux';
import { compose } from 'redux';

import {
  selectIsAuthenticatedUser,
  selectIsSudoedUser,
} from '@gandi/authentication';
import { selectPersonal } from '@gandi/redux-sharing';
import { withTranslator, type TranslatorProps } from '@gandi/translate';
import { Interactive, Svg } from '@gandi/ui';

import { withBasename, type WithBasenameProps } from 'src/helpers/withBasename';
import { withRouter, type WithRouterProps } from 'src/helpers/withRouter';

import { selectUserItems } from 'src/modules/app';
import type { FullState } from 'src/types';

import { useAppSelector } from '../../../hooks/useAppSelector';
import type { NavigationItems } from '../../types';
import { NavigationDropDown } from '../NavigationDropDown';

import styles from './UserWidget.scss';

type OwnProps = {
  className?: string,
};

type InjectedProps = {
  translator: TranslatorProps,
  ...WithRouterProps<>,
  ...WithBasenameProps,
};

type StateProps = {
  isAuthenticated: boolean,
  items: NavigationItems,
};

export type Props = {
  ...OwnProps,
  ...InjectedProps,
  ...StateProps,
};

const mapStateToProps = (
  state: FullState,
  {
    translator: { __ },
    location: { pathname, search },
    basename,
  }: InjectedProps,
): StateProps => ({
  isAuthenticated: selectIsAuthenticatedUser(state),
  items: selectUserItems(state, { __, basename, pathname, search }),
});

function UserName({
  isSudo,
  fullname,
}: {
  isSudo: boolean,
  fullname?: string,
}) {
  return (
    <>
      <span className={styles.circle}>
        <Svg name="user" size="5x" className={styles.svg} />
      </span>
      {isSudo && fullname}
    </>
  );
}

const userInfoItem = {
  title: 'username',
  icon: 'user',
  disabled: true,
  id: 'userInfo',
};

function UserWidget({
  isAuthenticated,
  items,
  className,
  translator,
}: Props): React.Node | Array<React.MixedElement> {
  const { __ } = translator;
  const ariaLabel = __(
    '<for-translators-use>Admin accessibility landmark</for-translators-use>User menu',
  );

  const isSudo = useAppSelector(selectIsSudoedUser);

  const {
    firstname,
    lastname,
    /** `firstname` & `lastname` may not be known yet before the first purchase;
     * but `name` is the username which is always defined when authenticated.
     */
    name,
  } = useAppSelector(selectPersonal) || {};
  const fullname = firstname && lastname ? `${firstname} ${lastname}` : null;

  if (!isAuthenticated) {
    // $FlowFixMe[missing-type-arg]
    return items.map<React.MixedElement>(item => (
      <Interactive
        key={`useritem-${item.title}`}
        href={item.href}
        flex
        paddingX="small"
        flexJustifyCenter
        flexAlignItemsCenter
        hoverable
        className={clsx(styles.root, styles.link, className)}
      >
        {item.title}
      </Interactive>
    ));
  }

  return (
    <NavigationDropDown
      id="user-menu"
      label={<UserName isSudo={isSudo} fullname={fullname || name} />}
      navigationItems={[userInfoItem, ...items]}
      aria-label={ariaLabel}
      className={clsx(styles.root, isSudo && styles.sudo, className)}
      placement="top-right"
    />
  );
}

export { UserWidget as Presenter };

export default (compose(
  withRouter,
  withBasename,
  withTranslator({ propsNamespace: 'translator' }),
  connect(mapStateToProps),
)(UserWidget): React.ComponentType<OwnProps>);
